/**
 * Landing Page — Desloca (redesign 2026-02).
 *
 * Inspirada no fluxo de conversão do Motoflix, mas mantendo a identidade
 * visual da Desloca (verde neon #00FF66 + fundo escuro asphalt-900).
 *
 * Estrutura:
 *  1. Header sticky com logo + menu + CTA "Alugar Agora"
 *  2. Hero (título grande, subtítulo, CTAs pra planos e WhatsApp)
 *  3. Faixa animada "Zero Burocracia..."
 *  4. Como Funciona (3 passos)
 *  5. Do Que Preciso (3 requisitos)
 *  6. Planos (3 cards com preço/dia)
 *  7. Diferenciais (4 ícones)
 *  8. Faixa animada de novo
 *  9. Footer com contato + redes
 *
 * Ao clicar em "Quero esse plano" → navega pra /interesse?plano=X.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, MessageCircle, CheckCircle2, Menu, X,
  Wrench, Shield, Receipt, Sparkles, AlertCircle, Clock,
  MapPin, IdCard, Zap, Gauge, Fuel, CircleDot, Disc,
} from 'lucide-react';


const API_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUM = '5513996208062';
const WHATSAPP_MSG = 'Olá, vi o site da Desloca e gostaria de saber mais sobre o aluguel de motos!';
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUM}?text=${encodeURIComponent(WHATSAPP_MSG)}`;

// Logo com efeito neon verde — combina melhor com o fundo escuro asphalt-900 desta landing.
const DESLOCA_LOGO_DARK = 'https://customer-assets.emergentagent.com/job_dcfaebc2-4990-416f-8985-7fe16afdbc64/artifacts/f24pja15_36610faa-61ce-4402-aacc-1fcef3dd6643.jpeg';

// Mascote da Desloca — PNG com fundo transparente (ou preto) — combina naturalmente com o hero.
const DESLOCA_MASCOTE = 'https://customer-assets.emergentagent.com/job_dcfaebc2-4990-416f-8985-7fe16afdbc64/artifacts/1mygitpx_IMG_5077.png';

const PLANOS = [
  {
    id: 'fidelidade',
    nome: 'Conquiste sua Moto',
    resumo: 'Ao final do contrato, a moto é sua.',
    diaria: 'R$ 60,71',
    semanal: 'R$ 425',
    beneficios: [
      'Contrato de 36 meses',
      'Todas as manutenções inclusas',
      'Moto segurada',
      'Sem burocracia e sem análise de crédito',
      'Ao final do contrato, a moto é sua',
    ],
    destaque: true,
    cta: 'Quero conquistar minha moto',
  },
  {
    id: '3meses',
    nome: 'Plano de 3 Meses',
    resumo: 'Ideal pra quem quer testar a locação sem compromisso longo.',
    diaria: 'R$ 57,14',
    semanal: 'R$ 400',
    beneficios: [
      'Contrato de 3 meses',
      'Troca de óleo inclusa',
      'Moto segurada',
      'Sem burocracia e sem análise de crédito',
    ],
    destaque: false,
    cta: 'Quero esse plano',
  },
  {
    id: 'anual',
    nome: 'Plano Anual',
    resumo: 'Melhor custo-benefício. Perfeito pra motoboys e delivery.',
    diaria: 'R$ 50,00',
    semanal: 'R$ 350',
    beneficios: [
      'Contrato de 12 meses',
      'Troca de óleo inclusa',
      'Moto segurada',
      'Sem burocracia e sem análise de crédito',
    ],
    destaque: false,
    cta: 'Escolher plano anual',
  },
];

const PASSOS_COMO_FUNCIONA = [
  {
    numero: '01',
    titulo: 'Escolha seu plano',
    texto: 'Selecione o plano que faz mais sentido pro seu dia-a-dia e clique em "Quero esse plano".',
  },
  {
    numero: '02',
    titulo: 'Preencha o formulário',
    texto: 'Preenchimento rápido de qualificação (2 minutos). Sem enrolação, sem análise de crédito.',
  },
  {
    numero: '03',
    titulo: 'Pague e retire a moto',
    texto: 'Para retirar você paga o caução de R$ 700 (devolvido ao final do contrato) + a primeira semana de aluguel. Depois, só o valor semanal.',
  },
  {
    numero: '04',
    titulo: 'Rode com a Desloca',
    texto: 'Após confirmação, agendamos a retirada pelo WhatsApp. Simples, rápido e sem burocracia.',
  },
];

const REQUISITOS = [
  {
    icone: MapPin,
    titulo: 'Residir na Baixada Santista',
    texto: 'Atendemos Santos, São Vicente, Praia Grande, Guarujá, Cubatão, Mongaguá, Itanhaém e Peruíbe.',
  },
  {
    icone: IdCard,
    titulo: 'CNH categoria A ou AB definitiva',
    texto: 'Sua CNH precisa estar dentro da validade e ser definitiva (não pode ser permissão provisória).',
  },
  {
    icone: MessageCircle,
    titulo: 'Fechamento pelo WhatsApp',
    texto: 'Após qualificação, nosso atendimento te ajuda a fechar o plano pelo WhatsApp.',
  },
];

const DIFERENCIAIS = [
  { icone: Wrench, titulo: 'Óleo por nossa conta', texto: 'Nos planos Mensal e Anual. Fidelidade: todas as manutenções.' },
  { icone: Shield, titulo: 'Proteção Total', texto: 'Motos seguradas e assistência.' },
  { icone: Receipt, titulo: 'Zero Impostos', texto: 'IPVA e Licenciamento por nossa conta.' },
  { icone: Sparkles, titulo: 'Zero Burocracia', texto: 'Sem análise de crédito.' },
];

const MOTO_IMG = 'https://customer-assets.emergentagent.com/job_dcfaebc2-4990-416f-8985-7fe16afdbc64/artifacts/560y8wf6_4df03cc5-6279-4ce2-b6db-2c608ed19678.jpeg';

const MOTO_SPECS = [
  { icone: Gauge, label: '162cc' },
  { icone: Zap, label: 'Partida Elétrica' },
  { icone: Fuel, label: '40 km/L' },
  { icone: Sparkles, label: 'Painel Digital' },
  { icone: Disc, label: 'Freios a Disco' },
  { icone: CircleDot, label: 'Zero Km' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [menuAberto, setMenuAberto] = useState(false);
  const [motosEsgotadas, setMotosEsgotadas] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/site/alertas`)
      .then(res => setMotosEsgotadas(res.data?.motos_esgotadas || false))
      .catch(() => {});
  }, []);

  const irParaPlano = (planoId) => {
    navigate(`/interesse?plano=${planoId}`);
    setMenuAberto(false);
  };

  return (
    <div className="min-h-screen bg-[#060A14] text-white overflow-x-hidden">
      {/* Alerta de motos esgotadas */}
      {motosEsgotadas && (
        <div
          onClick={() => navigate('/fila-espera')}
          className="bg-red-600 text-white py-3 px-4 cursor-pointer hover:bg-red-700 transition-colors"
          data-testid="alerta-motos-esgotadas"
        >
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 flex-wrap text-center">
            <AlertCircle className="w-5 h-5 animate-pulse" />
            <span className="font-bold text-sm sm:text-base">MOTOS ESGOTADAS</span>
            <span className="hidden sm:inline">•</span>
            <span className="text-sm">Entre na Fila de Espera</span>
            <Clock className="w-4 h-4" />
          </div>
        </div>
      )}

      {/* HEADER STICKY */}
      <header className="sticky top-0 z-50 bg-[#060A14]/95 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="landing-logo">
            <img
              src={DESLOCA_LOGO_DARK}
              alt="Desloca - Locadora de Motos"
              className="h-14 sm:h-16 w-auto object-contain"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#como-funciona" className="text-white/80 hover:text-[#00FF66] transition">Como Funciona</a>
            <a href="#a-moto" className="text-white/80 hover:text-[#00FF66] transition">A Moto</a>
            <a href="#planos" className="text-white/80 hover:text-[#00FF66] transition">Planos</a>
            <a href="#diferenciais" className="text-white/80 hover:text-[#00FF66] transition">Diferenciais</a>
            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="text-white/80 hover:text-[#00FF66] transition">Contato</a>
          </nav>

          <button
            onClick={() => irParaPlano('fidelidade')}
            className="hidden md:inline-flex items-center gap-2 bg-[#00FF66] text-[#060A14] font-bold px-5 py-2.5 rounded-full text-sm hover:brightness-110 transition shadow-lg shadow-[#00FF66]/20"
            data-testid="landing-cta-header"
          >
            Alugar Agora <ArrowRight className="w-4 h-4" />
          </button>

          <button
            onClick={() => setMenuAberto(v => !v)}
            className="md:hidden p-2 text-white"
            aria-label="Abrir menu"
            data-testid="landing-menu-toggle"
          >
            {menuAberto ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {menuAberto && (
          <div className="md:hidden border-t border-white/5 bg-[#060A14] px-4 py-4 space-y-3">
            <a onClick={() => setMenuAberto(false)} href="#como-funciona" className="block text-white/80 hover:text-[#00FF66] py-2">Como Funciona</a>
            <a onClick={() => setMenuAberto(false)} href="#a-moto" className="block text-white/80 hover:text-[#00FF66] py-2">A Moto</a>
            <a onClick={() => setMenuAberto(false)} href="#planos" className="block text-white/80 hover:text-[#00FF66] py-2">Planos</a>
            <a onClick={() => setMenuAberto(false)} href="#diferenciais" className="block text-white/80 hover:text-[#00FF66] py-2">Diferenciais</a>
            <a onClick={() => setMenuAberto(false)} href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="block text-white/80 hover:text-[#00FF66] py-2">Contato</a>
            <button
              onClick={() => irParaPlano('fidelidade')}
              className="w-full bg-[#00FF66] text-[#060A14] font-bold py-3 rounded-full text-sm"
              data-testid="landing-cta-mobile-menu"
            >
              Alugar Agora
            </button>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative py-16 sm:py-24 lg:py-32 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#00FF66]/5 to-transparent pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#00FF66]/10 rounded-full blur-3xl pointer-events-none" />

        {/* Mascote — PNG com fundo transparente/preto (blend natural com o hero #060A14) */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 flex items-end sm:items-center justify-end opacity-70 sm:opacity-90 lg:opacity-100"
        >
          <img
            src={DESLOCA_MASCOTE}
            alt=""
            className="h-[80%] sm:h-[85%] lg:h-[95%] w-auto object-contain object-right"
          />
        </div>

        {/* Vinheta radial pra suavizar a borda do mascote e melhorar contraste do texto */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-l from-transparent via-[#060A14]/40 to-[#060A14] sm:from-transparent sm:via-[#060A14]/20 sm:to-[#060A14]/80"
        />

        <div className="relative max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#00FF66]/10 border border-[#00FF66]/30 rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#00FF66] animate-pulse" />
            <span className="text-xs sm:text-sm text-[#00FF66] font-semibold uppercase tracking-wider">Baixada Santista • Sem análise de crédito</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black leading-tight mb-6">
            A melhor opção para <span className="text-[#00FF66]">locação de motos</span> na Baixada Santista
          </h1>

          <p className="text-base sm:text-xl text-white/70 max-w-2xl mx-auto mb-10">
            Locação de motos sem burocracia, com troca de óleo por nossa conta nos planos Mensal e Anual —
            e todas as manutenções inclusas no plano Fidelidade. Ideal pra motoboys, delivery, apps e locomoção do dia-a-dia.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="#planos"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#00FF66] text-[#060A14] font-bold px-8 py-4 rounded-full text-base hover:brightness-110 transition shadow-2xl shadow-[#00FF66]/25"
              data-testid="hero-cta-planos"
            >
              Ver planos agora <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white/5 border border-white/20 text-white font-bold px-8 py-4 rounded-full text-base hover:bg-white/10 transition"
              data-testid="hero-cta-whatsapp"
            >
              <MessageCircle className="w-4 h-4" /> Falar no WhatsApp
            </a>
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-white/50">
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#00FF66]" /> Zero burocracia</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#00FF66]" /> Troca de óleo inclusa</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#00FF66]" /> Motos seguradas</span>
          </div>
        </div>
      </section>

      {/* FAIXA ANIMADA */}
      <FaixaAnimada />

      {/* COMO FUNCIONA */}
      <section id="como-funciona" className="py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-xs uppercase tracking-widest text-[#00FF66] font-bold mb-3">Simples e rápido</h2>
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black">Como funciona?</h3>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PASSOS_COMO_FUNCIONA.map((p) => (
              <div
                key={p.numero}
                className="relative bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:border-[#00FF66]/40 transition group"
                data-testid={`passo-${p.numero}`}
              >
                <div className="text-6xl font-black text-[#00FF66]/20 group-hover:text-[#00FF66]/40 transition">{p.numero}</div>
                <h4 className="text-xl font-bold mt-2 mb-2">{p.titulo}</h4>
                <p className="text-white/70 text-sm leading-relaxed">{p.texto}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DO QUE PRECISO */}
      <section className="py-20 px-4 sm:px-6 bg-white/[0.02] border-y border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-xs uppercase tracking-widest text-[#00FF66] font-bold mb-3">Requisitos</h2>
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black">Do que preciso?</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {REQUISITOS.map((r, i) => (
              <div
                key={r.titulo}
                className="bg-[#060A14] border border-white/10 rounded-2xl p-6 flex flex-col items-start"
                data-testid={`requisito-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#00FF66]/10 border border-[#00FF66]/30 flex items-center justify-center mb-4">
                  <r.icone className="w-6 h-6 text-[#00FF66]" />
                </div>
                <h4 className="text-lg font-bold mb-2">{r.titulo}</h4>
                <p className="text-white/70 text-sm leading-relaxed">{r.texto}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLANOS */}
      <section id="planos" className="py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-xs uppercase tracking-widest text-[#00FF66] font-bold mb-3">Escolha seu plano</h2>
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black">Planos que se encaixam no seu ritmo</h3>
            <p className="text-white/60 mt-4 max-w-xl mx-auto">Confira nossos planos, escolha o que faz sentido pra você e preencha o formulário — é rápido.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PLANOS.map((p) => (
              <div
                key={p.id}
                className={`relative rounded-2xl p-6 flex flex-col ${
                  p.destaque
                    ? 'bg-gradient-to-b from-[#00FF66]/20 to-[#00FF66]/5 border-2 border-[#00FF66] shadow-2xl shadow-[#00FF66]/20 md:scale-105'
                    : 'bg-white/[0.03] border border-white/10'
                }`}
                data-testid={`plano-card-${p.id}`}
              >
                {p.destaque && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00FF66] text-[#060A14] text-xs font-black px-4 py-1 rounded-full uppercase tracking-wider">
                    Mais Popular
                  </div>
                )}
                <h4 className="text-xl font-black mb-1">{p.nome}</h4>
                <p className="text-white/60 text-sm mb-6 h-10">{p.resumo}</p>

                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-black text-[#00FF66]">{p.diaria}</span>
                    <span className="text-white/60 text-sm">/dia</span>
                  </div>
                  <p className="text-xs text-white/50 mt-1">Cobrança semanal • {p.semanal}/semana</p>
                </div>

                <ul className="space-y-2 mb-8 flex-1">
                  {p.beneficios.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-[#00FF66] shrink-0 mt-0.5" />
                      <span className="text-white/80">{b}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => irParaPlano(p.id)}
                  className={`w-full py-3 rounded-full font-bold text-sm transition ${
                    p.destaque
                      ? 'bg-[#00FF66] text-[#060A14] hover:brightness-110'
                      : 'bg-white/10 text-white border border-white/20 hover:bg-white/15'
                  }`}
                  data-testid={`plano-cta-${p.id}`}
                >
                  {p.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* A MOTO — DK 160 */}
      <section id="a-moto" className="py-20 px-4 sm:px-6 bg-white/[0.02] border-y border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-xs uppercase tracking-widest text-[#00FF66] font-bold mb-3">A Moto</h2>
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black">Conheça a DK 160</h3>
            <p className="text-white/60 mt-4 max-w-xl mx-auto">Moto zero km, econômica, robusta e ideal pra motoboys, delivery e locomoção do dia-a-dia.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-10 items-center">
            {/* Imagem */}
            <div className="relative">
              <div className="absolute inset-0 bg-[#00FF66]/10 blur-3xl rounded-full pointer-events-none" />
              <div className="relative bg-white rounded-3xl p-6 sm:p-10 shadow-2xl shadow-[#00FF66]/20">
                <img
                  src={MOTO_IMG}
                  alt="Moto DK 160 preta com detalhes vermelhos"
                  className="w-full h-auto object-contain"
                  data-testid="moto-img"
                />
              </div>
              <div className="absolute -top-3 -right-3 bg-[#00FF66] text-[#060A14] text-xs font-black px-4 py-1.5 rounded-full uppercase tracking-wider shadow-lg">
                Zero KM
              </div>
            </div>

            {/* Descrição + specs */}
            <div>
              <div className="inline-flex items-center gap-2 bg-[#00FF66]/10 border border-[#00FF66]/30 rounded-full px-3 py-1 mb-4">
                <span className="text-xs text-[#00FF66] font-bold uppercase tracking-wider">Modelo Disponível</span>
              </div>
              <h4 className="text-3xl sm:text-4xl font-black mb-3">DK 160</h4>
              <p className="text-white/70 text-base mb-8 leading-relaxed">
                A DK 160 é econômica, confiável e perfeita pra rodar o dia inteiro. Motor 162cc,
                painel digital, partida elétrica e ótimo consumo — tudo o que você precisa pra
                trabalhar ou se locomover na Baixada Santista.
              </p>

              <div className="grid grid-cols-2 gap-3 mb-8">
                {MOTO_SPECS.map((s, i) => (
                  <div
                    key={s.label}
                    className="flex items-center gap-3 bg-[#060A14] border border-white/10 rounded-xl px-4 py-3 hover:border-[#00FF66]/40 transition"
                    data-testid={`moto-spec-${i}`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-[#00FF66]/10 border border-[#00FF66]/30 flex items-center justify-center shrink-0">
                      <s.icone className="w-4 h-4 text-[#00FF66]" />
                    </div>
                    <span className="text-sm font-semibold text-white/90">{s.label}</span>
                  </div>
                ))}
              </div>

              <a
                href="#planos"
                className="inline-flex items-center gap-2 bg-[#00FF66] text-[#060A14] font-bold px-6 py-3 rounded-full text-sm hover:brightness-110 transition shadow-lg shadow-[#00FF66]/20"
                data-testid="moto-cta-planos"
              >
                Ver planos <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAIXA ANIMADA */}
      <FaixaAnimada />

      {/* DIFERENCIAIS */}
      <section id="diferenciais" className="py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-xs uppercase tracking-widest text-[#00FF66] font-bold mb-3">Vantagens</h2>
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black">Diferenciais Desloca</h3>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {DIFERENCIAIS.map((d, i) => (
              <div
                key={d.titulo}
                className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 text-center hover:border-[#00FF66]/40 transition"
                data-testid={`diferencial-${i}`}
              >
                <div className="w-14 h-14 mx-auto rounded-2xl bg-[#00FF66]/10 border border-[#00FF66]/30 flex items-center justify-center mb-4">
                  <d.icone className="w-7 h-7 text-[#00FF66]" />
                </div>
                <h4 className="text-base font-bold mb-1">{d.titulo}</h4>
                <p className="text-white/60 text-xs sm:text-sm">{d.texto}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href="#planos"
              className="inline-flex items-center gap-2 bg-[#00FF66] text-[#060A14] font-bold px-8 py-4 rounded-full text-base hover:brightness-110 transition shadow-2xl shadow-[#00FF66]/25"
              data-testid="cta-alugar-agora-final"
            >
              Alugar minha moto agora <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 py-10 px-4 sm:px-6 bg-white/[0.02]">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
          <div>
            <div className="mb-3">
              <img
                src={DESLOCA_LOGO_DARK}
                alt="Desloca - Locadora de Motos"
                className="h-16 w-auto object-contain"
              />
            </div>
            <p className="text-white/60 text-sm">Locação de motos sem burocracia na Baixada Santista.</p>
          </div>
          <div>
            <h5 className="text-sm font-bold mb-3 uppercase tracking-wider text-white/80">Navegue</h5>
            <ul className="space-y-2 text-sm text-white/60">
              <li><a href="#como-funciona" className="hover:text-[#00FF66]">Como Funciona</a></li>
              <li><a href="#planos" className="hover:text-[#00FF66]">Planos</a></li>
              <li><a href="#diferenciais" className="hover:text-[#00FF66]">Diferenciais</a></li>
              <li><Link to="/cliente" className="hover:text-[#00FF66]">App do Cliente</Link></li>
            </ul>
          </div>
          <div>
            <h5 className="text-sm font-bold mb-3 uppercase tracking-wider text-white/80">Contato</h5>
            <ul className="space-y-2 text-sm text-white/60">
              <li>
                <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-[#00FF66]">
                  <MessageCircle className="w-4 h-4" /> (13) 99620-8062
                </a>
              </li>
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0" /> Baixada Santista - SP</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/5 mt-8 pt-6 text-center text-xs text-white/40">
          DESLOCA © {new Date().getFullYear()}. Todos os direitos reservados.
        </div>
      </footer>

      {/* BOLHA FLUTUANTE WHATSAPP */}
      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-2xl shadow-[#25D366]/40 hover:scale-105 transition"
        aria-label="Falar no WhatsApp"
        data-testid="whatsapp-floating"
      >
        <MessageCircle className="w-7 h-7" />
      </a>
    </div>
  );
};

const FaixaAnimada = () => (
  <div className="py-3 bg-[#00FF66] text-[#060A14] overflow-hidden whitespace-nowrap select-none">
    <div className="flex gap-8 animate-marquee font-black text-sm uppercase tracking-wider">
      {Array.from({ length: 10 }).map((_, i) => (
        <span key={i} className="flex items-center gap-8 shrink-0">
          Zero Burocracia e Sem Análise de Crédito
          <span aria-hidden>•</span>
        </span>
      ))}
    </div>
    {/* CSS inline pro marquee */}
    <style>{`
      @keyframes marquee {
        0%   { transform: translateX(0); }
        100% { transform: translateX(-50%); }
      }
      .animate-marquee {
        animation: marquee 30s linear infinite;
        width: max-content;
      }
    `}</style>
  </div>
);

export default LandingPage;
